import { ADMIN_API_PREFIX, route } from "./routes.js";
import { z } from "zod";
//#region src/second-factor-policy.ts
/**
* `/v1/admin/second-factor-policy`: the org policy for the second factor at login (kernel apps,
* WS-34, R-154).
*
* The policy decides which members of an org must pass the second factor after the first factor:
* `all` members (the default of a new org) or only the `privileged` ones (owners and holders of a
* tier-1 or tier-2 key, I-7).
*
* Producer: core-api's `SecondFactorPolicyController` in
* `cloud/dp/core-api/src/auth/second-factor/` (WS-10a). Consumers: the policy card of the org
* admin app (EX-1d), which starts after an SDK release carries this module (R-154 item 3), and the
* kernel shell (WS-15). core-api reads the org from the JWT only, so neither route takes an org id.
* `NoDelegatedPrincipalGuard` refuses a delegated token on both routes with 403.
*
* Kernel class (`CONTRACT_ROUTE_KERNEL_CLASS` in `route-census.ts`): right `identity.providers`,
* the read at tier 0 and the write at tier 1. These are the classes that core-api's
* `NON_CONTRACT_KERNEL_ROUTES` rows gave the two routes before the move (R-054), so the move into the
* census changes no guard decision. The two routes sit in the kernel fold `KERNEL_CONTRACT_ROUTES`, as
* the family `second-factor-policy`.
*
* Direction rule (R-28): the request schema is `z.strictObject` and refuses an unknown key; the
* response schema is `z.object` and strips one. No schema here carries a `.transform()`, so
* `createOpenApiDocument()` can represent each one.
*/
/** The route path. It has no parameter, so the OpenAPI form and the request form agree. */
const ADMIN_SECOND_FACTOR_POLICY_PATH = `${ADMIN_API_PREFIX}/second-factor-policy`;
/**
* The two org policies, in this order, frozen:
*
* - `all`: every member passes the second factor at login.
* - `privileged`: only owners and holders of a tier-1 or tier-2 key pass it.
*
* The values equal core-api's `SECOND_FACTOR_POLICIES` and the `auth.organizations` column check.
*/
const SECOND_FACTOR_POLICIES = Object.freeze(["all", "privileged"]);
/**
* The parse of one policy value. `all` and `privileged` pass unchanged. Any other value fails,
* case included: `ALL`, `none`, an empty text, `null` and a number each fail. No `.transform()`.
*/
const secondFactorPolicySchema = z.enum(SECOND_FACTOR_POLICIES);
/**
* `GET /v1/admin/second-factor-policy`. Reads the policy of the caller's org.
*
* - 200 with {@link secondFactorPolicyResponseSchema}. An org that never changed the policy
*   answers `{ policy: 'all' }`, never 404.
* - 401 `Unknown organization` when no org row has the JWT's org id.
* - 403 for a caller without `settings:identity:read`, or with a delegated token.
*
* Permission `settings:identity:read`. Kernel class `identity.providers` tier 0: the kernel guard
* (WS-19) asks no step-up.
*/
const GET_SECOND_FACTOR_POLICY_ROUTE = route("GET", ADMIN_SECOND_FACTOR_POLICY_PATH);
/**
* `PUT /v1/admin/second-factor-policy`. Stores the policy of the caller's org.
*
* - Body {@link putSecondFactorPolicyRequestSchema}.
* - 200 with {@link secondFactorPolicyResponseSchema}: the stored policy. A body equal to the
*   stored policy answers 200 too. core-api emits one audit event `second_factor_policy_changed`
*   with `{ from, to }` for each 200, a write of the current value included.
* - 400 from the pipe for a body outside the schema.
* - 403 for a caller without `settings:identity:manage`, with a delegated token, or that is not a
*   stored owner of the org (core-api's `SecondFactorPolicyService.write`). An org id with no org
*   row has no stored owner, so it answers 403 too.
* - 500 when the audit emit fails. core-api then puts the previous policy back.
*
* Kernel class `identity.providers` tier 1: the kernel guard (WS-19) asks a step-up per call, and
* the step-up create route (WS-12a) accepts a request for this route because the census holds it.
*/
const PUT_SECOND_FACTOR_POLICY_ROUTE = route("PUT", ADMIN_SECOND_FACTOR_POLICY_PATH);
/**
* The body of {@link PUT_SECOND_FACTOR_POLICY_ROUTE}, strict: `{ policy }`, where `policy` is
* `all` or `privileged`. A missing `policy`, any other value and any other key fail.
*/
const putSecondFactorPolicyRequestSchema = z.strictObject({ policy: secondFactorPolicySchema });
/**
* The 200 body of both routes: `{ policy }`, `all` or `privileged`. A member that a newer core-api
* adds is stripped (R-28). A missing or unknown `policy` fails the parse.
*/
const secondFactorPolicyResponseSchema = z.object({ policy: secondFactorPolicySchema });
/** Every route this file declares, `GET` then `PUT`, for the kernel fold of `route-census.ts`. */
const secondFactorPolicyRoutes = Object.freeze([GET_SECOND_FACTOR_POLICY_ROUTE, PUT_SECOND_FACTOR_POLICY_ROUTE]);
//#endregion
export { ADMIN_SECOND_FACTOR_POLICY_PATH, GET_SECOND_FACTOR_POLICY_ROUTE, PUT_SECOND_FACTOR_POLICY_ROUTE, SECOND_FACTOR_POLICIES, putSecondFactorPolicyRequestSchema, secondFactorPolicyResponseSchema, secondFactorPolicyRoutes, secondFactorPolicySchema };

//# sourceMappingURL=second-factor-policy.js.map